'use client'

import React, { useState, useMemo } from 'react'
import { cn } from '../../../lib/utils'
import Icon, { type iconTypes } from '../Icon'
import { Input } from '../Input'
import { TabsWrapper, TabsList, TabsTrigger, TabsContent } from '../Tabs'
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '../Tooltip'

export type ToolItem = {
  id: string
  name: string
  displayName: string
  description: string
  kind: string
}

export type ToolGroup = {
  groupName: string
  groupType: 'model' | 'app' | 'system'
  tools: ToolItem[]
}

export type ToolMarketplaceProps = {
  groups: ToolGroup[]
  searchQuery?: string
  onSearchChange?: (query: string) => void
  onToolSelect: (tool: ToolItem) => void
  selectedToolId?: string
  isLoading?: boolean
  emptyMessage?: string
  tabs?: {
    value: string
    onValueChange: (value: string) => void
    items: Array<{ label: string; value: string }>
  }
  title?: string
}

function getToolIcon(kind: string): iconTypes {
  switch (kind) {
    case 'SYSTEM':
      return 'Database'
    case 'AGENT':
      return 'Bot'
    case 'MCP':
      return 'Plug'
    case 'INTERNAL':
      return 'Workflow'
    default:
      return 'Wrench'
  }
}

function getToolKindColor(kind: string): string {
  switch (kind) {
    case 'SYSTEM':
      return 'text-blue-500 border-blue-500/30 hover:border-blue-500/60 hover:bg-blue-500/10'
    case 'AGENT':
      return 'text-purple-500 border-purple-500/30 hover:border-purple-500/60 hover:bg-purple-500/10'
    case 'MCP':
      return 'text-green-500 border-green-500/30 hover:border-green-500/60 hover:bg-green-500/10'
    case 'INTERNAL':
      return 'text-orange-500 border-orange-500/30 hover:border-orange-500/60 hover:bg-orange-500/10'
    default:
      return 'text-muted-foreground border-border hover:border-primary/60 hover:bg-accent'
  }
}

type ToolChipProps = {
  tool: ToolItem
  isSelected?: boolean
  onClick: () => void
}

const ToolChip: React.FC<ToolChipProps> = ({ tool, isSelected, onClick }) => {
  return (
    <TooltipProvider delayDuration={300}>
      <Tooltip>
        <TooltipTrigger asChild>
          <button
            onClick={onClick}
            className={cn(
              'inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md border text-xs font-medium transition-all cursor-pointer',
              getToolKindColor(tool.kind),
              {
                'ring-2 ring-primary ring-offset-1 ring-offset-background':
                  isSelected,
              },
            )}
          >
            <Icon name={getToolIcon(tool.kind)} size={12} />
            <span className="truncate max-w-[120px]">{tool.displayName}</span>
          </button>
        </TooltipTrigger>
        <TooltipContent side="bottom" className="max-w-[250px]">
          <p className="font-medium">{tool.displayName}</p>
          {tool.description && (
            <p className="text-xs text-muted-foreground mt-1">
              {tool.description}
            </p>
          )}
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  )
}

type ToolGroupSectionProps = {
  group: ToolGroup
  selectedToolId?: string
  onToolSelect: (tool: ToolItem) => void
  defaultExpanded?: boolean
}

const ToolGroupSection: React.FC<ToolGroupSectionProps> = ({
  group,
  selectedToolId,
  onToolSelect,
  defaultExpanded = true,
}) => {
  const [isExpanded, setIsExpanded] = useState(defaultExpanded)

  const getGroupIcon = () => {
    switch (group.groupType) {
      case 'model':
        return 'Database'
      case 'system':
        return 'Settings'
      case 'app':
        return 'Plug'
      default:
        return 'Wrench'
    }
  }

  return (
    <div className="border-b border-border last:border-b-0">
      <button
        onClick={() => setIsExpanded(!isExpanded)}
        className="flex items-center justify-between w-full px-3 py-2 hover:bg-accent/50 transition-colors"
      >
        <div className="flex items-center gap-2">
          <Icon
            name={getGroupIcon()}
            size={14}
            className="text-muted-foreground"
          />
          <span className="text-sm font-medium">{group.groupName}</span>
          <span className="text-xs text-muted-foreground">
            ({group.tools.length})
          </span>
        </div>
        <Icon
          name={isExpanded ? 'ChevronDown' : 'ChevronRight'}
          size={14}
          className="text-muted-foreground"
        />
      </button>
      {isExpanded && (
        <div className="px-3 pb-3 pt-1">
          <div className="flex flex-wrap gap-1.5">
            {group.tools.map((tool) => (
              <ToolChip
                key={tool.id}
                tool={tool}
                isSelected={selectedToolId === tool.id}
                onClick={() => onToolSelect(tool)}
              />
            ))}
          </div>
        </div>
      )}
    </div>
  )
}

export const ToolMarketplace: React.FC<ToolMarketplaceProps> = ({
  groups,
  searchQuery = '',
  onSearchChange,
  onToolSelect,
  selectedToolId,
  isLoading = false,
  emptyMessage = 'No tools available',
  tabs,
  title,
}) => {
  const [internalSearchQuery, setInternalSearchQuery] = useState('')

  const effectiveSearchQuery = onSearchChange
    ? searchQuery
    : internalSearchQuery
  const handleSearchChange = onSearchChange || setInternalSearchQuery

  const filteredGroups = useMemo(() => {
    if (!effectiveSearchQuery) return groups

    const query = effectiveSearchQuery.toLowerCase()
    return groups
      .map((group) => ({
        ...group,
        tools: group.tools.filter(
          (tool) =>
            tool.displayName.toLowerCase().includes(query) ||
            tool.description.toLowerCase().includes(query) ||
            tool.name.toLowerCase().includes(query) ||
            group.groupName.toLowerCase().includes(query),
        ),
      }))
      .filter((group) => group.tools.length > 0)
  }, [groups, effectiveSearchQuery])

  const renderContent = () => {
    if (isLoading) {
      return (
        <div className="flex flex-col gap-3 p-4">
          {[1, 2, 3].map((i) => (
            <div key={i} className="animate-pulse">
              <div className="h-4 w-24 bg-muted rounded mb-2" />
              <div className="flex gap-2">
                <div className="h-7 w-16 bg-muted rounded" />
                <div className="h-7 w-20 bg-muted rounded" />
                <div className="h-7 w-14 bg-muted rounded" />
              </div>
            </div>
          ))}
        </div>
      )
    }

    if (filteredGroups.length === 0) {
      return (
        <div className="flex flex-col items-center justify-center py-12 px-4 text-center">
          <Icon
            name="Search"
            size={32}
            className="text-muted-foreground mb-3"
          />
          <p className="text-sm text-muted-foreground">
            {effectiveSearchQuery
              ? `No tools found matching "${effectiveSearchQuery}"`
              : emptyMessage}
          </p>
        </div>
      )
    }

    return (
      <div className="flex flex-col">
        {filteredGroups.map((group) => (
          <ToolGroupSection
            key={`${group.groupType}-${group.groupName}`}
            group={group}
            selectedToolId={selectedToolId}
            onToolSelect={onToolSelect}
          />
        ))}
      </div>
    )
  }

  const content = (
    <div className="flex flex-col h-full">
      {title && (
        <div className="px-4 py-3 border-b border-border">
          <h3 className="text-sm font-semibold">{title}</h3>
        </div>
      )}

      <div className="px-4 py-3 border-b border-border">
        <Input
          id="tool-marketplace-search"
          type="text"
          placeholder="Search tools..."
          leftIcon="Search"
          value={effectiveSearchQuery}
          handleChange={(e) => handleSearchChange(e.target.value)}
        />
      </div>

      {tabs ? (
        <TabsWrapper
          value={tabs.value}
          onValueChange={tabs.onValueChange}
          className="flex-1 flex flex-col"
        >
          <div className="px-4 py-2 border-b border-border">
            <TabsList>
              {tabs.items.map((tab) => (
                <TabsTrigger key={tab.value} value={tab.value}>
                  {tab.label}
                </TabsTrigger>
              ))}
            </TabsList>
          </div>
          {tabs.items.map((tab) => (
            <TabsContent
              key={tab.value}
              value={tab.value}
              className="flex-1 overflow-auto"
            >
              {renderContent()}
            </TabsContent>
          ))}
        </TabsWrapper>
      ) : (
        <div className="flex-1 overflow-auto">{renderContent()}</div>
      )}
    </div>
  )

  return content
}

export default ToolMarketplace
